/**
 * Операции над PDF, выполняемые целиком в браузере.
 *
 * Файл человека никуда не отправляется: и разбор, и сборка происходят в
 * его вкладке. Поэтому здесь нет ни одного обращения к сети, а модуль не
 * зависит от React и проверяется тестами напрямую.
 *
 * `pdf-lib` умеет собирать документ, но не умеет рисовать страницы, а
 * `pdfjs-dist` наоборот. Разбор на две библиотеки намеренный: рендер
 * миниатюр нужен только в браузере с canvas, и в тестах он не участвует.
 */
import { PDFDocument, degrees, rgb } from 'pdf-lib';

/** Размер чистого листа в пунктах. A4 — то, на чём печатают. */
export const BLANK_SIZE = { width: 595.28, height: 841.89 };

/** Одна страница будущего документа: откуда взята и как повёрнута. */
export interface PageRef {
  /** Устойчивый идентификатор для списка и drag-and-drop. */
  id: string;
  /**
   * Индекс исходного файла в `sources`, либо -1 у чистого листа.
   *
   * Пустая страница описана тем же типом, что и взятая из файла, ради
   * одного свойства: её можно двигать, поворачивать, удалять и
   * подписывать теми же действиями. Отдельный тип заставил бы
   * разветвлять каждое из них.
   */
  fileIndex: number;
  /** Номер страницы внутри исходного файла, с нуля. У чистого листа не значит ничего. */
  pageIndex: number;
  /** Дополнительный поворот к собственному повороту страницы, в градусах. */
  rotation: number;
}

/** Чистый лист, который встанет в документ наравне с остальными. */
export function blankPage(): PageRef {
  return { id: newId(), fileIndex: -1, pageIndex: 0, rotation: 0 };
}

/**
 * Текст, картинка или заливка, наложенные поверх страницы.
 *
 * Заливка нужна ради правки чужого текста. Переписать строку
 * внутри готового PDF в общем случае нельзя: текст там не абзац, а
 * поток команд рисования глифов с отдельными координатами и часто
 * с урезанным встроенным шрифтом, в котором нужных букв просто нет.
 * Закрасить старое и написать сверху даёт человеку тот же результат
 * честным способом.
 */
export interface Overlay {
  id: string;
  /** Идентификатор страницы, к которой привязано наложение. */
  pageId: string;
  /** Доля ширины страницы, 0..1. Доли, а не пункты: страницы разного размера. */
  x: number;
  /** Доля высоты страницы, 0..1, считая сверху. */
  y: number;
  kind: 'text' | 'image' | 'fill' | 'draw';
  /** Для `text` — сама надпись, для `image` — data URL картинки, для `fill` и `draw` — пусто. */
  value: string;
  /** Кегль в пунктах. Только для текста. */
  size: number;
  /** Цвет `#rrggbb`: цвет текста либо цвет заливки. */
  color: string;
  /** Ширина в долях ширины страницы, 0..1. Для картинки и заливки. */
  width: number;
  /** Высота заливки в долях высоты страницы, 0..1. Только для `fill`. */
  height?: number;
  /**
   * Точки росчерка кистью в долях страницы. Только для `draw`.
   *
   * Линия хранится точками, а не растром: она остаётся чёткой при любом
   * увеличении готового файла и весит десяток чисел вместо картинки во
   * всю страницу.
   */
  points?: { x: number; y: number }[];
}

export interface BuildInput {
  /** Исходные файлы в том порядке, в каком на них ссылается `pages`. */
  sources: ArrayBuffer[];
  pages: PageRef[];
  overlays: Overlay[];
  /**
   * Как превратить надпись в картинку.
   *
   * Текст кладётся на страницу картинкой, а не шрифтом: встроенные шрифты
   * pdf-lib знают только WinAnsi, и любая кириллица или сербская латиница
   * с диакритикой роняли бы сборку прямо в момент сохранения. Отрисовка
   * средствами браузера снимает вопрос выбора шрифта целиком.
   *
   * Функция передаётся снаружи, потому что в тестах canvas нет.
   */
  renderText: (overlay: Overlay) => Promise<{ png: Uint8Array; width: number; height: number }>;
}

export function newId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Цвет `#rrggbb` в долях 0..1, как их ждёт pdf-lib. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  // Непонятный цвет становится белым, а не чёрным: заливка нужна, чтобы
  // скрыть старый текст на белом листе, и чёрная полоса вместо белой
  // испортила бы страницу заметнее, чем отсутствие заливки.
  if (!match) return { r: 1, g: 1, b: 1 };
  const value = parseInt(match[1] ?? 'ffffff', 16);
  return {
    r: ((value >> 16) & 255) / 255,
    g: ((value >> 8) & 255) / 255,
    b: (value & 255) / 255,
  };
}

/** Страницы одного загруженного файла в исходном порядке. */
export async function pagesOfFile(bytes: ArrayBuffer, fileIndex: number): Promise<PageRef[]> {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  return doc.getPageIndices().map((pageIndex) => ({
    id: newId(),
    fileIndex,
    pageIndex,
    rotation: 0,
  }));
}

/**
 * Разбор диапазона страниц вида `1-3, 7, 10-12`.
 *
 * Возвращает номера с нуля, без повторов и в порядке возрастания. Всё,
 * что выходит за пределы документа или записано неразборчиво, молча
 * отбрасывается: показывать человеку ошибку разбора на каждый introduced
 * символ во время набора хуже, чем ничего не выделять.
 */
export function parseRange(input: string, total: number): number[] {
  const out = new Set<number>();

  for (const part of input.split(',')) {
    const chunk = part.trim();
    if (chunk === '') continue;

    const dash = /^(\d+)\s*-\s*(\d+)$/.exec(chunk);
    if (dash) {
      const from = Number(dash[1]);
      const to = Number(dash[2]);
      if (from < 1 || to < from) continue;
      for (let n = from; n <= Math.min(to, total); n += 1) out.add(n - 1);
      continue;
    }

    const single = /^(\d+)$/.exec(chunk);
    if (single) {
      const n = Number(single[1]);
      if (n >= 1 && n <= total) out.add(n - 1);
    }
  }

  return [...out].sort((a, b) => a - b);
}

/**
 * Собирает итоговый документ.
 *
 * Страницы копируются из исходных файлов по одной, в порядке `pages`:
 * так удаление, перестановка, поворот и слияние нескольких файлов
 * оказываются одной и той же операцией, а не тремя разными.
 */
export async function buildPdf({
  sources,
  pages,
  overlays,
  renderText,
}: BuildInput): Promise<Uint8Array> {
  const out = await PDFDocument.create();

  const loaded = await Promise.all(
    sources.map((bytes) => PDFDocument.load(bytes, { ignoreEncryption: true })),
  );

  for (const ref of pages) {
    let page;

    if (ref.fileIndex < 0) {
      page = out.addPage([BLANK_SIZE.width, BLANK_SIZE.height]);
      if (ref.rotation !== 0) page.setRotation(degrees(ref.rotation % 360));
    } else {
      const source = loaded[ref.fileIndex];
      if (!source) continue;

      const [copied] = await out.copyPages(source, [ref.pageIndex]);
      if (!copied) continue;
      if (ref.rotation !== 0) {
        copied.setRotation(degrees((copied.getRotation().angle + ref.rotation) % 360));
      }
      page = out.addPage(copied);
    }

    for (const overlay of overlays.filter((item) => item.pageId === ref.id)) {
      const { width: pw, height: ph } = page.getSize();

      if (overlay.kind === 'fill') {
        // Заливка рисуется прямоугольником, а не картинкой: это ровный
        // цвет, и гнать его через canvas значило бы раздувать файл
        // растром там, где хватает четырёх чисел.
        const { r, g, b } = hexToRgb(overlay.color);
        const width = pw * overlay.width;
        const height = ph * (overlay.height ?? 0.03);
        page.drawRectangle({
          x: overlay.x * pw,
          y: ph - overlay.y * ph - height,
          width,
          height,
          color: rgb(r, g, b),
        });
        continue;
      }

      if (overlay.kind === 'draw') {
        const points = overlay.points ?? [];
        const { r, g, b } = hexToRgb(overlay.color);
        // Отрезками между соседними точками, а не кривой: браузер
        // отдаёт след курсора достаточно частым, чтобы ломаная
        // выглядела линией, а сглаживание исказило бы подпись.
        for (let i = 1; i < points.length; i += 1) {
          const from = points[i - 1];
          const to = points[i];
          if (!from || !to) continue;
          page.drawLine({
            start: { x: from.x * pw, y: ph - from.y * ph },
            end: { x: to.x * pw, y: ph - to.y * ph },
            thickness: overlay.size,
            color: rgb(r, g, b),
          });
        }
        continue;
      }

      if (overlay.kind === 'image') {
        const bytes = dataUrlToBytes(overlay.value);
        if (!bytes) continue;
        const image = overlay.value.startsWith('data:image/jpeg')
          ? await out.embedJpg(bytes)
          : await out.embedPng(bytes);
        const width = pw * overlay.width;
        const height = (image.height / image.width) * width;
        page.drawImage(image, {
          x: overlay.x * pw,
          y: ph - overlay.y * ph - height,
          width,
          height,
        });
        continue;
      }

      const { png, width, height } = await renderText(overlay);
      const image = await out.embedPng(png);
      // Картинка рисуется в двойном разрешении ради чёткости, поэтому на
      // страницу кладётся половина её пиксельного размера.
      const w = width / 2;
      const h = height / 2;
      page.drawImage(image, {
        x: overlay.x * pw,
        y: ph - overlay.y * ph - h,
        width: w,
        height: h,
      });
    }
  }

  return out.save();
}

/** Байты из `data:` URL. `null`, если строка не похожа на data URL. */
export function dataUrlToBytes(url: string): Uint8Array | null {
  const comma = url.indexOf(',');
  if (!url.startsWith('data:') || comma === -1) return null;
  const binary = atob(url.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
